import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { AggregateSearchResultModel } from '@shared/model/aggreegate-search-result-model';
import { DecimalPipe, NgIf, SlicePipe } from '@angular/common';
import { ESearchTab } from '@app/pages/search/models/search-tab.enum';
import { RouterLink } from '@angular/router';
import { EImageType, ImagePipe } from '@shared/pipes/image.pipe';
import { htmlUrls } from '@shared/utils/router-paths';
import { HighlightPipe } from '@shared/pipes/highlight-pipe/highlight.pipe';
import { AuthorTypePipe } from '@shared/pipes/author-type/author-type.pipe';
import { AuthorSortPipe } from '@shared/pipes/author-type/author-sort.pipe';
import { FollowBtnComponent } from '../follow-btn/follow-btn.component';
import { FollowUserDataPipe, IFollowStateData } from '../follow-btn/pipe/follow-user-data.pipe';
import { animate, style, transition, trigger } from '@angular/animations';
import { SEARCH_HISTORY } from 'constants/storage-keys';

@Component({
  selector: 'beh-layout-search',
  imports: [
    ReactiveFormsModule,
    OverlayModule,
    NgxSkeletonLoaderModule,
    DecimalPipe,
    RouterLink,
    ImagePipe,
    HighlightPipe,
    AuthorTypePipe,
    AuthorSortPipe,
    FollowBtnComponent,
    FollowUserDataPipe,
    SlicePipe,
    NgIf
  ],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleY(0.9)' }),
        animate('120ms ease-out', style({ opacity: 1, transform: 'scaleY(1)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'scaleY(0.95)' })),
      ]),
    ]),
  ],
  templateUrl: './layout-search.html',
  styleUrl: './layout-search.scss'
})
export class LayoutSearch extends BaseComponent implements OnChanges, OnInit {

  @ViewChild(CdkOverlayOrigin) origin!: CdkOverlayOrigin;
  @Input() loading = false;
  @Input() result!: AggregateSearchResultModel;
  @Output() termChanged = new EventEmitter<string>();
  @Output() followUser = new EventEmitter<IFollowStateData>();
  @Output() navigateToSearch = new EventEmitter<{ cat: ESearchTab, term: string }>();

  termControl = new FormControl('', [Validators.minLength(2), Validators.required]);
  panelOpen = false;
  imageType = EImageType;
  routes = htmlUrls;
  searchTabEnum = ESearchTab;
  searchHistory: string[] = [];

  constructor() { super() }

  ngOnChanges(changes: SimpleChanges): void {
    this.checkPanelCondition(changes['result']?.currentValue, changes['loading']?.currentValue);
  }

  checkPanelCondition(r: AggregateSearchResultModel, loading: boolean) {
    if (loading || (r && r.totalResult! > 0))
      this.openPanel();
    else
      this.closePanel();
  }


  ngOnInit(): void {
    this.getSearchHistory();
    this.handleTermChange().subscribe();
  }


  handleTermChange() {
    return this.termControl.valueChanges.pipe(
      tap(() => this.closePanel()),
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
      tap(v => {
        this.termChanged.emit(v!);
      })
    );
  }

  openPanel() {
    if (this.loading || (this.result && this.result.totalResult! > 0) || this.searchHistory?.length > 0)
      this.panelOpen = true;
  }

  closePanel() {
    this.panelOpen = false;
  }

  navigateToSearchPage(cat: ESearchTab) {
    if (this.termControl.valid) {
      const term = this.termControl.value!
      this.navigateToSearch.emit({ cat, term });
      this.setHistoryItem(term);
      this.closePanel();
    }
  }

  clearSearch() {
    this.termControl.reset();
  }

  setHistoryItem(term: string) {
    this.searchHistory?.unshift(term);
    this.Repository.setLocalItem(SEARCH_HISTORY, JSON.stringify(this.searchHistory));
  }

  removeHistoryItem(index: number) {
    this.searchHistory.splice(index, 1);
    this.Repository.setLocalItem(SEARCH_HISTORY, JSON.stringify(this.searchHistory));
  }

  getSearchHistory() {
    this.searchHistory = JSON.parse(this.Repository.getLocalItem(SEARCH_HISTORY)!) ?? [];
  }

  loadHistory(h: string) {
    this.termControl.setValue(h, { emitEvent: false });
    this.navigateToSearchPage(ESearchTab.books);
  }

  FollowClicked(user: IFollowStateData) {
    this.followUser.emit(user);
  }
}
